import { useEffect, useState } from 'react';
import api from '../../../services/api';
import type { UspsVettingReport } from '../tabs/uspsVetting.types';

/**
 * Loads the USPS vetting report for a carrier. The summary strip and the full tab both use this, so
 * opening a carrier makes one request, not two: in-flight and recent results are shared for a few
 * minutes (the backend also caches for an hour).
 */
const SHARE_MS = 5 * 60 * 1000;
const shared = new Map<string, { at: number; promise: Promise<UspsVettingReport> }>();

export const errorMessage = (e: unknown): string => {
  const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
  if (msg) return msg;
  return e instanceof Error ? e.message : 'Could not load the USPS vetting report';
};

function fetchReport(dotNumber: string): Promise<UspsVettingReport> {
  const hit = shared.get(dotNumber);
  if (hit && Date.now() - hit.at < SHARE_MS) return hit.promise;
  const promise = api.get(`/usps-vetting/${dotNumber}`).then((res) => res.data as unknown as UspsVettingReport);
  shared.set(dotNumber, { at: Date.now(), promise });
  // A failed request must not be reused: let the next caller retry.
  promise.catch(() => shared.delete(dotNumber));
  return promise;
}

export function useUspsVetting(dotNumber: string): { report: UspsVettingReport | null; loading: boolean; error: string | null } {
  const [report, setReport] = useState<UspsVettingReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setReport(null);
    fetchReport(dotNumber)
      .then((r) => {
        if (!cancelled) setReport(r);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(errorMessage(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dotNumber]);

  return { report, loading, error };
}
